"use client";

import * as React from "react";
import { XIcon } from "lucide-react";
import { cn, Badge, Button } from "visuais";

type InputTagsProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange"
> & {
  value: string[];
  onChange: React.Dispatch<React.SetStateAction<string[]>>;
};

const InputTags = React.forwardRef<HTMLInputElement, InputTagsProps>(
  ({ className, value, onChange, disabled, ...props }, ref) => {
    const [pendingDataPoint, setPendingDataPoint] = React.useState("");

    const addTagsFromInput = React.useCallback(
      (inputString: string) => {
        const newTags = inputString
          .split(",")
          .map((chunk) => chunk.trim())
          .filter((tag) => tag.length > 0);

        if (newTags.length > 0) {
          onChange((prevValue) =>
            Array.from(new Set([...prevValue, ...newTags]))
          );
        }
      },
      [onChange]
    );

    React.useEffect(() => {
      if (pendingDataPoint.includes(",")) {
        addTagsFromInput(pendingDataPoint);
        setPendingDataPoint("");
      }
    }, [pendingDataPoint, addTagsFromInput]);

    const handleAddPendingDataPoint = () => {
      if (pendingDataPoint.trim()) {
        addTagsFromInput(pendingDataPoint);
        setPendingDataPoint("");
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" || e.key === ",") {
        e.preventDefault();
        handleAddPendingDataPoint();
      } else if (
        e.key === "Backspace" &&
        pendingDataPoint.length === 0 &&
        value.length > 0
      ) {
        e.preventDefault();
        onChange((prevValue) => prevValue.slice(0, -1));
      }
    };

    return (
      <div
        className={cn(
          "flex min-h-10 w-full flex-wrap items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
          "has-[input:focus-visible]:outline-none has-[input:focus-visible]:ring-2 has-[input:focus-visible]:ring-ring has-[input:focus-visible]:ring-offset-2",
          disabled ? "cursor-not-allowed opacity-50" : "",
          className
        )}
      >
        {value.map((item) => (
          <Badge key={item} variant="secondary" className="whitespace-nowrap">
            {item}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="ml-1.5 h-4 w-4 hover:bg-destructive/20"
              onClick={() => {
                onChange(value.filter((i) => i !== item));
              }}
              disabled={disabled}
            >
              <XIcon className="h-3 w-3" />
            </Button>
          </Badge>
        ))}
        <input
          className="min-w-[60px] flex-1 bg-transparent py-1 outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
          value={pendingDataPoint}
          onChange={(e) => setPendingDataPoint(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleAddPendingDataPoint}
          disabled={disabled}
          {...props}
          ref={ref}
        />
      </div>
    );
  }
);

InputTags.displayName = "InputTags";

export { InputTags };
